/**
 * Safety Element Colors Store — user-customisable colors per element type
 *
 * @module core/modules/safety/store/element-colors-store
 *
 * @description
 * Zustand store with localStorage persistence. Keeps only user overrides;
 * falls back to SAFETY_ELEMENT_COLORS defaults for untouched entries.
 */

import { create } from 'zustand';
import { devtools, persist } from 'zustand/middleware';
import { SAFETY_ELEMENT_COLORS, type SafetyElementId } from '../constants/element-colors';

interface SafetyElementColorsState {
  /** Only colours the user has explicitly changed */
  overrides: Partial<Record<SafetyElementId, string>>;
}

interface SafetyElementColorsActions {
  /** Return override if set, otherwise default */
  getColor: (id: SafetyElementId) => string;
  /** Set a user override */
  setColor: (id: SafetyElementId, hex: string) => void;
  /** Remove a single override (revert to default) */
  resetColor: (id: SafetyElementId) => void;
  /** Remove all overrides */
  resetAll: () => void;
}

export type SafetyElementColorsStore = SafetyElementColorsState & SafetyElementColorsActions;

export const useSafetyElementColorsStore = create<SafetyElementColorsStore>()(
  devtools(
    persist(
      (set, get) => ({
        overrides: {},

        getColor(id: SafetyElementId): string {
          return get().overrides[id] ?? SAFETY_ELEMENT_COLORS[id];
        },

        setColor(id: SafetyElementId, hex: string): void {
          set(
            (s) => ({ overrides: { ...s.overrides, [id]: hex } }),
            false,
            'setColor',
          );
        },

        resetColor(id: SafetyElementId): void {
          set(
            (s) => {
              const next = { ...s.overrides };
              delete next[id];
              return { overrides: next };
            },
            false,
            'resetColor',
          );
        },

        resetAll(): void {
          set({ overrides: {} }, false, 'resetAll');
        },
      }),
      { name: 'safety-element-colors' },
    ),
    { name: 'SafetyElementColors' },
  ),
);
